import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgPromotionApi } from '@/services/org-promotion.api';
import { OrgAffidavitRow, OrgReportsApi } from '@/services/org-reports.api';
import { OrgAffidavitsWriteApi, UpdateAffidavitData } from '@/services/org-affidavits.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { formatDate } from '@/lib/format';

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

/**
 * Org tier — real Affidavit management (create/edit/delete), for any
 * school in scope. The read-only cross-school report (Reports > Affidavits)
 * already exists separately — this is the write counterpart, scoped to
 * one school at a time (writes need a schema, reports don't).
 */
export function OrgAffidavitsManagePage() {
  const qc = useQueryClient();
  const [schoolId, setSchoolId] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<OrgAffidavitRow | null>(null);
  const [deleting, setDeleting] = useState<OrgAffidavitRow | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-affidavits-manage'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['org-affidavits-manage', schoolId],
    queryFn: () => OrgReportsApi.affidavits({ schoolId }),
    enabled: !!schoolId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['org-affidavits-manage', schoolId] });

  const remove = useMutation({
    mutationFn: (id: string) => OrgAffidavitsWriteApi.remove(id, schoolId),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
    },
  });

  return (
    <>
      <PageHeader
        title="Affidavit"
        description="Create, edit, and delete student/guardian signed affidavits for a school in your organization."
        actions={
          schoolId && (
            <button className="btn-primary flex items-center gap-1.5" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Create Affidavit
            </button>
          )
        }
      />

      <div className="mb-4">
        <Select className="!w-64" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">— Select a school —</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      {!schoolId ? (
        <div className="card p-8 text-center text-sm text-slate-400">Select a school to manage its affidavits.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Student</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Class</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Guardian</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Signatures</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isFetching && !rows.length ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                    No affidavits yet for this school.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-slate-700">{r.studentName}</td>
                    <td className="px-4 py-2.5 text-slate-600">{r.className}</td>
                    <td className="px-4 py-2.5 text-slate-600">{r.guardianName ?? '—'}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        {r.studentSignatureUrl ? (
                          <img src={r.studentSignatureUrl} alt="Student signature" title="Student signature" className="h-8 w-14 rounded border border-slate-200 object-contain" />
                        ) : (
                          <span className="text-xs text-slate-400">no student sig</span>
                        )}
                        {r.guardianSignatureUrl ? (
                          <img src={r.guardianSignatureUrl} alt="Guardian signature" title="Guardian signature" className="h-8 w-14 rounded border border-slate-200 object-contain" />
                        ) : (
                          <span className="text-xs text-slate-400">no guardian sig</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{formatDate(r.createdAt)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="inline-flex items-center gap-2">
                        <button type="button" title="Edit" className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700" onClick={() => setEditing(r)}>
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button type="button" title="Delete" className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600" onClick={() => setDeleting(r)}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="Create Affidavit" size="md">
        <CreateAffidavitForm schoolId={schoolId} onDone={() => { setCreating(false); invalidate(); }} />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Affidavit" size="md">
        {editing && <EditAffidavitForm schoolId={schoolId} affidavit={editing} onDone={() => { setEditing(null); invalidate(); }} />}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        title="Delete affidavit?"
        message={`This will permanently remove ${deleting?.studentName ?? 'this'}'s affidavit and both signature files. This cannot be undone.`}
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}

// ───── Signature upload control — shared by create/edit forms ──────────────
function SignatureUpload({
  schoolId,
  label,
  imageUrl,
  onChange,
}: {
  schoolId: string;
  label: string;
  imageUrl: string;
  onChange: (url: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();

  async function handlePick(file: File) {
    setUploading(true);
    setUploadError(undefined);
    try {
      const res = await OrgAffidavitsWriteApi.upload(schoolId, file);
      onChange(res.url);
    } catch (e) {
      setUploadError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }

  return (
    <Field label={label} required={!imageUrl}>
      {imageUrl ? (
        <div className="relative">
          <img src={imageUrl} alt="" className="h-24 w-full rounded-md border border-slate-200 object-contain" />
          <label className="absolute inset-x-0 bottom-0 flex cursor-pointer items-center justify-center gap-1.5 rounded-b-md bg-black/50 py-1 text-xs text-white hover:bg-black/60">
            <ImagePlus className="h-3.5 w-3.5" /> Replace
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handlePick(e.target.files[0])} />
          </label>
        </div>
      ) : (
        <label className="flex h-24 w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-slate-300 text-xs text-slate-500 hover:border-brand-400 hover:text-brand-600">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          {uploading ? 'Uploading…' : 'Upload signature'}
          <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => e.target.files?.[0] && handlePick(e.target.files[0])} />
        </label>
      )}
      {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
    </Field>
  );
}

// ───── Class → Section → Student cascading picker ───────────────────────────
function ClassSectionStudentPicker({
  schoolId,
  classId,
  onClassChange,
  sectionId,
  onSectionChange,
  studentId,
  onStudentChange,
}: {
  schoolId: string;
  classId: string;
  onClassChange: (v: string) => void;
  sectionId: string;
  onSectionChange: (v: string) => void;
  studentId: string;
  onStudentChange: (v: string) => void;
}) {
  const { data: classes = [] } = useQuery({
    queryKey: ['org-affidavits-manage-classes', schoolId],
    queryFn: () => OrgPromotionApi.classesForSchool(schoolId),
  });
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: students = [] } = useQuery({
    queryKey: ['org-affidavits-manage-students', schoolId, sectionId],
    queryFn: () => OrgReportsApi.affidavitStudentsBySection(schoolId, sectionId),
    enabled: !!sectionId,
  });

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Field label="Class" required>
        <Select
          value={classId}
          onChange={(e) => {
            onClassChange(e.target.value);
            onSectionChange('');
            onStudentChange('');
          }}
        >
          <option value="">Select class</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Section" required>
        <Select
          value={sectionId}
          onChange={(e) => {
            onSectionChange(e.target.value);
            onStudentChange('');
          }}
          disabled={!classId}
        >
          <option value="">Select section</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Student" required>
        <Select value={studentId} onChange={(e) => onStudentChange(e.target.value)} disabled={!sectionId}>
          <option value="">Select student</option>
          {students.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

function CreateAffidavitForm({ schoolId, onDone }: { schoolId: string; onDone: () => void }) {
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [studentSignatureUrl, setStudentSignatureUrl] = useState('');
  const [guardianSignatureUrl, setGuardianSignatureUrl] = useState('');

  const create = useMutation({
    mutationFn: () => OrgAffidavitsWriteApi.create({ schoolId, studentId, studentSignatureUrl, guardianSignatureUrl }),
    onSuccess: onDone,
  });

  const valid = !!studentId && !!studentSignatureUrl && !!guardianSignatureUrl;

  return (
    <div className="space-y-4">
      <ClassSectionStudentPicker
        schoolId={schoolId}
        classId={classId}
        onClassChange={setClassId}
        sectionId={sectionId}
        onSectionChange={setSectionId}
        studentId={studentId}
        onStudentChange={setStudentId}
      />
      <p className="text-xs text-slate-500">The guardian is resolved automatically from the selected student.</p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SignatureUpload schoolId={schoolId} label="Student Signature" imageUrl={studentSignatureUrl} onChange={setStudentSignatureUrl} />
        <SignatureUpload schoolId={schoolId} label="Guardian Signature" imageUrl={guardianSignatureUrl} onChange={setGuardianSignatureUrl} />
      </div>

      {create.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(create.error)}</div>}

      <div className="flex justify-end gap-2 pt-2">
        <button className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
        <button className="btn-primary" disabled={!valid || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? 'Saving…' : 'Create'}
        </button>
      </div>
    </div>
  );
}

function EditAffidavitForm({ schoolId, affidavit, onDone }: { schoolId: string; affidavit: OrgAffidavitRow; onDone: () => void }) {
  const [studentSignatureUrl, setStudentSignatureUrl] = useState(affidavit.studentSignatureUrl ?? '');
  const [guardianSignatureUrl, setGuardianSignatureUrl] = useState(affidavit.guardianSignatureUrl ?? '');

  const save = useMutation({
    mutationFn: () => {
      const data: UpdateAffidavitData = { schoolId };
      if (studentSignatureUrl !== affidavit.studentSignatureUrl) data.studentSignatureUrl = studentSignatureUrl;
      if (guardianSignatureUrl !== affidavit.guardianSignatureUrl) data.guardianSignatureUrl = guardianSignatureUrl;
      return OrgAffidavitsWriteApi.update(affidavit.id, data);
    },
    onSuccess: onDone,
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        {affidavit.studentName} · {affidavit.className}
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SignatureUpload schoolId={schoolId} label="Student Signature" imageUrl={studentSignatureUrl} onChange={setStudentSignatureUrl} />
        <SignatureUpload schoolId={schoolId} label="Guardian Signature" imageUrl={guardianSignatureUrl} onChange={setGuardianSignatureUrl} />
      </div>

      {save.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(save.error)}</div>}

      <div className="flex justify-end gap-2 pt-2">
        <button className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
        <button className="btn-primary" disabled={!studentSignatureUrl || !guardianSignatureUrl || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
